import React, { useState, useEffect, useRef } from "react";
import { fetchScraperLogs, startScraping } from "../services/envDataService";

const WebscrapingComponent = () => {
  const [usersData, setUsersData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [scraping, setScraping] = useState(false);
  const intervalRef = useRef(null);

  useEffect(() => {
    loadScraperLogs();
    
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);

  const loadScraperLogs = async (showLoading = true) => {
    try {
      if (showLoading) {
        setLoading(true);
      }
      setError(null);
      const result = await fetchScraperLogs();

      if (result.success && result.data) {
        const transformedData = result.data.map((item) => ({
          id: item.id,
          name: item.name,
          run_id: item.run_id,
          date: new Date(item.execution_date).toISOString().replace('T', ' ').replace('Z', ''),
          fetchData: `${item.record_count} records`,
          status: item.status,
        }));
        setUsersData(transformedData);

        // Check if there's any record that's NOT Success or failure
        const hasActiveJobs = transformedData.some(
          (item) => item.status !== "Success" && item.status !== "failure"
        );

        // Stop auto-refresh if all jobs are complete
        if (!hasActiveJobs && intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
      } else {
        setError(result.message || "Failed to fetch data");
      }
    } catch (err) {
      setError(err.message || "An error occurred while fetching data");
      console.error("Error fetching scraper logs:", err);
    } finally {
      if (showLoading) {
        setLoading(false);
      }
    }
  };

  const handleStartScraping = async () => {
    try {
      setScraping(true);
      setError(null);
      const response = await startScraping();

      if (response.success) {
        await loadScraperLogs(true);
        
        // Start auto-refresh - keep fetching until all jobs are done
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
        }
        
        intervalRef.current = setInterval(() => {
          loadScraperLogs(false); // false = no loading spinner during auto-refresh
        }, 3000); // Check every 3 seconds
      } else {
        setError(response.message || "Failed to start scraping");
      }
    } catch (err) {
      console.error("Error starting scraping:", err);
      setError("Failed to start scraping. Please try again.");
    } finally {
      setScraping(false);
    }
  };

  return (
    <div className="flex flex-col sm:items-center gap-6">
      <div className="flex flex-col md:flex-row bg-primary-light p-8 rounded-lg w-full max-w-[1000px]">
        <div className="md:w-[40%] flex justify-center items-center">
          <img
            src="/images/web-scrap.png"
            alt="webscraping"
            width="600"
            height="400"
            className="w-full max-w-[450px] rounded-lg"
          />
        </div>

        <div className="md:w-[60%] w-full mt-6 md:mt-0 md:pl-8">
          <div className="bg-white rounded-lg shadow-md overflow-hidden">
            {loading ? (
              <div className="flex items-center justify-center h-[400px]">
                <div className="text-gray-500">Loading scraper logs...</div>
              </div>
            ) : error ? (
              <div className="flex flex-col items-center justify-center h-[400px] gap-4">
                <div className="text-red-500">Error: {error}</div>
                <button
                  onClick={loadScraperLogs}
                  className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                >
                  Retry
                </button>
              </div>
            ) : usersData.length === 0 ? (
              <div className="flex items-center justify-center h-[400px]">
                <div className="text-gray-500">No scraper logs found</div>
              </div>
            ) : (
              <div className="max-h-[400px] overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
                <table className="min-w-full text-sm">
                  <thead className="bg-[#F4F4F4] text-gray-700 font-semibold sticky top-0 z-10">
                    <tr>
                      <th className="px-4 py-3 text-left">Date</th>
                      <th className="px-4 py-3 text-left">Name</th>
                      <th className="px-4 py-3 text-left">No of Records</th>
                      <th className="px-4 py-3 text-left">Status</th>
                      <th className="px-4 py-3 text-left">Run ID</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {usersData.map((user) => (
                      <tr
                        key={user.id}
                        className="hover:bg-gray-50 transition-colors"
                      >
                        <td className="px-4 py-3">{user.date}</td>
                        <td className="px-4 py-3">{user.name}</td>
                        <td className="px-4 py-3">{user.fetchData}</td>
                        <td className="px-4 py-3">
                          <span
                            className={`px-3 py-1 rounded-full text-xs font-medium ${
                              user.status === "Success"
                                ? "bg-green-100 text-green-700"
                                : user.status === "failure"
                                ? "bg-red-100 text-red-700"
                                : "bg-yellow-100 text-yellow-700"
                            }`}
                          >
                            {user.status}
                          </span>
                        </td>
                        <td className="px-4 py-3">{user.run_id}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
      <div className="m-auto mt-4">
        <button
          onClick={handleStartScraping}
          disabled={scraping}
          className={`common-btn flex items-center gap-3 m-auto ${
            scraping ? "opacity-60 cursor-not-allowed" : ""
          }`}
        >
          {scraping ? (
            <>
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              <span>Starting...</span>
            </>
          ) : (
            <>
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M22.3671 1.81905C22.3585 1.77767 22.3384 1.73959 22.3089 1.70926C22.2795 1.67894 22.2421 1.65762 22.201 1.64782C19.4609 0.977879 13.1296 3.36525 9.69947 6.79402C9.08763 7.4009 8.52993 8.05989 8.03257 8.76361C6.97479 8.67004 5.91701 8.74817 5.01549 9.14115C2.47184 10.2607 1.73126 13.1819 1.52494 14.4385C1.51324 14.5072 1.51765 14.5778 1.53784 14.6446C1.55802 14.7114 1.59343 14.7725 1.64127 14.8234C1.6891 14.8741 1.74807 14.9131 1.81353 14.9373C1.87899 14.9614 1.94915 14.97 2.01851 14.9624L6.10321 14.5119C6.10611 14.8199 6.12469 15.1275 6.15889 15.4335C6.17942 15.6461 6.27379 15.8447 6.42555 15.995L8.00731 17.573C8.15767 17.7245 8.35622 17.8189 8.56871 17.8396C8.87304 17.8737 9.17896 17.8923 9.4852 17.8953L9.03701 21.9748C9.02953 22.0442 9.03818 22.1143 9.06237 22.1797C9.08656 22.2451 9.12553 22.3041 9.17629 22.3519C9.22705 22.3997 9.2882 22.4351 9.35496 22.4553C9.42167 22.4755 9.49222 22.48 9.56099 22.4684C10.8153 22.2672 13.7416 21.5266 14.8546 18.983C15.2475 18.0815 15.328 17.0289 15.2372 15.9762C15.9427 15.4788 16.6034 14.9209 17.212 14.3089C20.6529 10.8852 23.0267 4.69531 22.3671 1.81905Z"
                  fill="white"
                />
              </svg>
              <span>Start Scraping</span>
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default WebscrapingComponent;